import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import { EditorButton } from '~/editor/ui/buttons/EditorButton';
import { CloseButton } from '~/ui/buttons/CloseButton';

export type EditingMode = 'private' | 'live';

type WalkthroughTooltip = {
  title: string;
  body: string;
  position: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  arrow: string;
  anchor?: string;
  isEditingNotice?: boolean;
};

const TOP_RIGHT_ARROW_CENTER_FROM_RIGHT = 30;
const TOOLTIP_BORDER_WIDTH = 2;
const BEAK_HALF_WIDTH = 8;
const BEAK_OFFSET_FROM_PADDING_EDGE =
  TOP_RIGHT_ARROW_CENTER_FROM_RIGHT - TOOLTIP_BORDER_WIDTH - BEAK_HALF_WIDTH;
const TOOLTIP_TEXT_INSET = 16;
const CLOSE_BUTTON_INSET = 8;
const TOOLTIP_BACKGROUND = '#fffde8';
const TOOLTIP_BORDER = '#000';

const rightOffsetUnder = (anchor: string): string | null => {
  const target = document.querySelector(`[data-tour="${anchor}"]`);
  if (!target) return null;

  const { left, width } = target.getBoundingClientRect();
  if (width === 0) return null;

  const fromRight =
    window.innerWidth - (left + width / 2) - TOP_RIGHT_ARROW_CENTER_FROM_RIGHT;
  return `${Math.round(fromRight)}px`;
};

const EDITING_MODE_TOOLTIPS: Record<
  EditingMode,
  { title: string; body: string }
> = {
  private: {
    title: 'You’re editing privately',
    body:
      'Current learners will finish the version they started, while new assignments will receive the updated course once published.\n\n' +
      'Existing translations will update automatically when you publish.'
  },
  live: {
    title: 'You’re editing a live course',
    body:
      'Changes apply immediately to everyone, including in-progress learners.\n\n' +
      'Added translations for a previous version? Update them again using the Translations console after you finish your changes.'
  }
};

const TooltipContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 9999;
  /* Let clicks / lower UI (e.g. EditorBanners) work outside the yellow tooltip box */
  pointer-events: none;

  .tooltip-container {
    z-index: 1000;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 411px;
    max-width: calc(100vw - 40px);
    padding: ${TOOLTIP_TEXT_INSET - TOOLTIP_BORDER_WIDTH}px;
    background-color: ${TOOLTIP_BACKGROUND};
    color: #000;
    border: ${TOOLTIP_BORDER_WIDTH}px solid ${TOOLTIP_BORDER};
    border-radius: 16px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    filter: drop-shadow(0 15px 10px rgba(0, 0, 0, 0.05))
      drop-shadow(0 30px 15px rgba(0, 0, 0, 0.03))
      drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.05));
    pointer-events: auto;
  }

  #tooltip-title {
    padding-right: 24px;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  .tooltip-body-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    white-space: pre-line;
  }

  .tooltip-actions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
  }

  .closeBtn {
    top: ${CLOSE_BUTTON_INSET - TOOLTIP_BORDER_WIDTH}px;
    right: ${CLOSE_BUTTON_INSET - TOOLTIP_BORDER_WIDTH}px;
    left: auto;
    padding: 4.5px;

    svg path {
      stroke: #000;
    }
  }

  .tooltip-arrow {
    position: absolute;
    pointer-events: none;

    ::before,
    ::after {
      content: '';
      position: absolute;
      border: ${BEAK_HALF_WIDTH}px solid transparent;
    }
  }

  .tooltip-arrow[data-placement='top-right'],
  .tooltip-arrow[data-placement='bottom-left'] {
    width: 16px;
    height: 10.5px;

    ::before,
    ::after {
      left: 0;
    }
  }

  .tooltip-arrow[data-placement='top-right'] {
    top: -10.5px;
    right: ${BEAK_OFFSET_FROM_PADDING_EDGE}px;

    ::before {
      top: -8px;
      border-bottom-color: ${TOOLTIP_BORDER};
    }

    ::after {
      top: -5.5px;
      border-bottom-color: ${TOOLTIP_BACKGROUND};
    }
  }

  .tooltip-arrow[data-placement='bottom-left'] {
    bottom: -10.5px;
    left: ${BEAK_OFFSET_FROM_PADDING_EDGE}px;

    ::before {
      top: 2.5px;
      border-top-color: ${TOOLTIP_BORDER};
    }

    ::after {
      top: 0;
      border-top-color: ${TOOLTIP_BACKGROUND};
    }
  }

  .tooltip-arrow[data-placement='left'] {
    left: -10.5px;
    top: ${BEAK_OFFSET_FROM_PADDING_EDGE}px;
    width: 10.5px;
    height: 16px;

    ::before,
    ::after {
      top: 0;
    }

    ::before {
      left: -8px;
      border-right-color: ${TOOLTIP_BORDER};
    }

    ::after {
      left: -5.5px;
      border-right-color: ${TOOLTIP_BACKGROUND};
    }
  }
`;

const Tooltips = ({
  editingMode,
  productTour = false
}: {
  editingMode?: EditingMode | null;
  productTour?: boolean;
}) => {
  const {
    showTooltips,
    tooltip,
    nextTooltip,
    prevTooltip,
    setShowTooltips,
    training
  } = useEditorStore();

  useEffect(() => {
    if (window && window.document) {
      setTimeout(() => {
        const element = window.document.getElementById('tooltip-title');
        if (element) {
          element.focus();
        }
      }, 0);
    }
  }, [tooltip]);

  useEffect(() => {
    if (!showTooltips) return undefined;

    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowTooltips(false);
    };

    document.addEventListener('keydown', dismissOnEscape);
    return () => document.removeEventListener('keydown', dismissOnEscape);
  }, [showTooltips, setShowTooltips]);

  const tooltipList = useMemo<WalkthroughTooltip[]>(() => {
    const fullList = [
      {
        title: 'Publish',
        body: 'Once you are done editing, publish the course here, and assign it to your Learners.',
        position: {
          right: '50px',
          top: '75px'
        },
        arrow: 'top-right' as const,
        anchor: 'primary-action'
      },
      {
        // Step index must stay === COMPLIANCE_CONTENT_TOOLTIP_INDEX (tourConstants.ts)
        title: 'Legal Tags',
        body:
          'Legal Tags identify content that may be tied to compliance requirements.\n\n' +
          'For some courses, Legal Tags appear on specific slides. For others, Legal Tags may appear on all slides throughout the course.\n\n' +
          'Before removing or substantially modifying tagged content, consider any applicable legal or regulatory requirements.',
        position: {
          left: '255px',
          bottom: '100px'
        },
        arrow: 'bottom-left' as const
      },
      {
        title: 'Navigation',
        body: 'Browse your course outline here, organized into chapters and slides. Simply click any slide to view its content. Need to reorganize? Just drag and drop slides to create your perfect learning sequence.',
        position: {
          left: '255px',
          top: '100px'
        },
        arrow: 'left' as const
      },
      {
        title: 'Adding Slides',
        body: 'Click to add new slides or chapters. Choose from various templates with images, videos, or interactive quizzes.',
        position: {
          left: '255px',
          top: '16px'
        },
        arrow: 'left' as const
      },
      {
        title: 'Narrations',
        body: 'Bring your slide to life with voice. Upload audio files, or generate speech from text using our natural-sounding AI voices.',
        position: {
          right: '295px',
          top: '75px'
        },
        arrow: 'top-right' as const,
        anchor: 'slide-narration'
      },
      {
        title: 'Changing Images',
        body: 'Click this Edit icon to open the Media modal, where you can select and change background images or videos.',
        position: {
          right: '5px',
          top: '125px'
        },
        arrow: 'top-right' as const
      }
    ];

    const standardTooltipList = training?.customizationOfCanonical
      ? fullList.map(item => {
          const canonicalPublishTitle =
            'Your changes are automatically saved while you edit.\n\n' +
            'Publishing makes those saved changes available as the customized version of the course.\n\n' +
            "⚠️ After publishing, you'll choose whether the original EasyLlama course or your customized version is Active for learners.";

          return item.title === 'Publish'
            ? { ...item, body: canonicalPublishTitle }
            : item;
        })
      : fullList.filter(item => item.title !== 'Legal Tags');

    if (!editingMode) return standardTooltipList;

    const editingNotice: WalkthroughTooltip = {
      ...EDITING_MODE_TOOLTIPS[editingMode],
      position: { right: '20px', top: '89px' },
      arrow: 'none',
      isEditingNotice: true
    };

    return productTour
      ? [...standardTooltipList, editingNotice]
      : [editingNotice];
  }, [editingMode, productTour, training?.customizationOfCanonical]);

  const lastTooltipIndex = tooltipList.length - 1;
  const activeTooltipIndex =
    editingMode && !productTour
      ? lastTooltipIndex
      : Math.min(tooltip, lastTooltipIndex);
  const activeTooltip = tooltipList[activeTooltipIndex];
  const activeAnchor = activeTooltip.anchor;
  const isEditingNotice = Boolean(activeTooltip.isEditingNotice);

  const [anchoredRight, setAnchoredRight] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!showTooltips || !activeAnchor) {
      setAnchoredRight(null);
      return undefined;
    }

    const placeUnderAnchor = () =>
      setAnchoredRight(rightOffsetUnder(activeAnchor));
    placeUnderAnchor();
    const headerChanges = new MutationObserver(placeUnderAnchor);
    headerChanges.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
    window.addEventListener('resize', placeUnderAnchor);
    return () => {
      headerChanges.disconnect();
      window.removeEventListener('resize', placeUnderAnchor);
    };
  }, [showTooltips, activeAnchor]);

  if (!showTooltips) return null;

  return (
    <TooltipContainer role="dialog" aria-labelledby="tooltip-title">
      <Tooltip
        placement="none"
        top={activeTooltip.position.top}
        bottom={activeTooltip.position.bottom}
        left={activeTooltip.position.left}
        right={anchoredRight ?? activeTooltip.position.right}
        className="tooltip-container"
      >
        <CloseButton
          type="close"
          onClick={() => setShowTooltips(false)}
          autoFocus={false}
        />
        <b id="tooltip-title" tabIndex={0} role="heading" aria-level={2}>
          {activeTooltip.title}
        </b>
        <span className="tooltip-body-text">{activeTooltip.body}</span>
        <div className="tooltip-actions">
          {activeTooltipIndex > 0 && (
            <EditorButton variant="outline" onClick={prevTooltip}>
              Prev
            </EditorButton>
          )}
          {activeTooltipIndex < lastTooltipIndex && (
            <EditorButton variant="primary" onClick={nextTooltip}>
              Next
            </EditorButton>
          )}
          {activeTooltipIndex === lastTooltipIndex && (
            <EditorButton
              variant="primary"
              onClick={() => setShowTooltips(false)}
            >
              {isEditingNotice ? 'Got it' : 'Got It'}
            </EditorButton>
          )}
        </div>
        {activeTooltip.arrow !== 'none' && (
          <span
            className="tooltip-arrow"
            data-placement={activeTooltip.arrow}
            aria-hidden="true"
          />
        )}
      </Tooltip>
    </TooltipContainer>
  );
};

export default Tooltips;
